<template>
  <div class="c-invite-confirmation">
    <div v-if="isConfirmedInvite">
      <p class="title">
        Confirmação enviada em {{ dateInviteConfirmation | dateWithoutHours }}.
      </p>
      <p class="description final">
        Caso deseje alterar a confirmação, entre em contato com o responsável
        pelo evento.
      </p>
    </div>
    <div v-else>
      <p class="title">Informe quem irá comparecer:</p>
      <p class="description">
        Após envio da confirmação da presença, não será possível realizar
        alterações.
      </p>
      <p class="count-tag" v-if="invite.max_guests > 1">
        Você pode confirmar até {{ invite.max_guests }} convidados
      </p>
      <p class="count-tag" v-else>
        Você pode confirmar até {{ invite.max_guests }} convidado
      </p>
    </div>

    <form @submit.prevent="submit">
      <div
        v-for="(guest, index) in guests"
        :key="`guest-${index}`"
        class="guest"
      >
        <div class="field">
          <BaseInputOutlined
            v-if="guest.id && guest.name"
            :value="guest.name"
            disabled
            class="has-text-left"
            label="<strong>Nome completo:</strong>"
          />
          <BaseInputOutlined
            v-else
            v-model="guest.name"
            class="has-text-left"
            label="<strong>Nome completo:</strong>"
            :disabled="isConfirmedInvite"
            :hasError="getGuestFieldErrorByIndex('name', index)"
            :error="
              getGuestFieldErrorByIndex('name', index)
                ? 'Este campo não pode ficar em branco.'
                : ''
            "
          />
        </div>
        <div class="field">
          <BaseSelectOutlined
            v-model="guest.type"
            class="has-text-left"
            label="<strong>Grupo de idade:</strong>"
            :disabled="isConfirmedInvite"
            :options="agesList"
            :hasError="getGuestFieldErrorByIndex('type', index)"
            :error="
              getGuestFieldErrorByIndex('type', index)
                ? 'Este campo não pode ficar em branco.'
                : ''
            "
          />
        </div>
        <div class="field">
          <BaseSelectOutlined
            v-model="guest.confirmed"
            class="has-text-left"
            label="<strong>Status de confirmação:</strong>"
            :disabled="isConfirmedInvite"
            :options="statusList"
            :hasError="getGuestFieldErrorByIndex('confirmed', index)"
            :error="
              getGuestFieldErrorByIndex('confirmed', index)
                ? 'Este campo não pode ficar em branco.'
                : ''
            "
          />
        </div>
        <div v-if="index < guests.length - 1" class="separator"></div>
      </div>

      <div v-if="!isConfirmedInvite" class="field add-guest">
        <button
          v-if="guests.length < invite.max_guests"
          @click="addGuest"
          type="button"
          class="link icon-left"
        >
          <base-icon width="24px" height="24px" name="painel/plus-circle" />
          Adicionar novo convidado
        </button>
        <p v-else class="error-label">
          Você atingiu a quantidade máxima de convidados permitidos para
          <strong>{{ invite.name }}</strong
          >.
        </p>
      </div>

      <div
        v-if="configs?.preferences?.closed?.show_obs && !isConfirmedInvite"
        class="field"
      >
        <BaseTextareaOutlined
          v-model="obs"
          class="has-text-left"
          label="<strong>Adicionar observação:</strong> (Opcional)"
          :rows="3"
          :maxlength="160"
          :hasError="validation.hasError('obs')"
          :error="
            validation.hasError('obs') ? validation.firstError('obs') : ''
          "
        />
      </div>

      <div v-if="!isConfirmedInvite" class="field">
        <BaseInputOutlined
          v-model="email"
          type="email"
          class="has-text-left"
          autocomplete
          label="<strong>E-mail:</strong> (Preenchimento obrigatório).<br>Você receberá a confirmação de presença neste e-mail."
          :hasError="validation.hasError('email')"
          :error="
            validation.hasError('email')
              ? 'Por favor, insira um endereço de e-mail válido.'
              : ''
          "
        />
      </div>

      <div v-if="!isConfirmedInvite" class="field">
        <ResponsabilityBox
          v-bind:value.sync="use_terms"
          class="has-text-left"
          label="Declaro que tive acesso, li e concordo com os Termos de uso e Política de Privacidade do Casar.com."
          :has-error="validation.hasError('use_terms')"
        />
      </div>

      <button
        v-if="!isConfirmedInvite"
        :disabled="!canSubmit"
        type="submit"
        class="button is-primary"
      >
        Enviar confirmação
      </button>
    </form>
  </div>
</template>

<script>
import SimpleVueValidator from 'simple-vue-validator'
import BaseInputOutlined from '@/components/BaseInputOutlined.vue'
import BaseTextareaOutlined from '@/components/BaseTextareaOutlined.vue'
import BaseSelectOutlined from '@/components/BaseSelectOutlined.vue'
import ResponsabilityBox from '../Wallet/Pix/ResponsabilityBox.vue'
import RsvpApi from '@/lib/api/Rsvp'

const Validator = SimpleVueValidator.Validator

const GUEST = {
  name: '',
  type: '',
  confirmed: '',
}

export default {
  mixins: [SimpleVueValidator.mixin],

  props: {
    invite: {
      type: Object,
      required: true,
    },
    configs: {
      type: Object,
      required: true,
    },
  },

  components: {
    BaseInputOutlined,
    BaseTextareaOutlined,
    BaseSelectOutlined,
    ResponsabilityBox,
  },

  validators: {
    email: (value) => {
      return Validator.value(value).required().email()
    },
    use_terms: (value) => {
      return Validator.value(value).custom(() => {
        return !value
      })
    },
    obs: (value) => {
      return Validator.value(value).maxLength(
        160,
        'Por favor, insira informações com até 160 caracteres.'
      )
    },
  },

  computed: {
    canSubmit() {
      return (
        !this.validation.hasError('obs') &&
        this.validation.isPassed('email') &&
        this.validation.isPassed('use_terms') &&
        this.guestsTouched &&
        !this.guestErrors.has &&
        !this.loading &&
        !this.isConfirmedInvite
      )
    },

    guestErrors() {
      let has = false
      const errors = []

      this.guests.forEach((guest, index) => {
        errors.push({
          name: !guest.name,
          type: !guest.type,
          confirmed: !guest.confirmed || guest.confirmed === 'maybe',
        })

        const hasError =
          !guest.name ||
          !guest.type ||
          !guest.confirmed ||
          guest.confirmed === 'maybe'

        if (this.guests.length > 1 && hasError && !this.isEmptyGuest(guest))
          has = true
        else if (hasError) has = true
      })

      return {
        has,
        errors,
      }
    },

    guestsTouched() {
      return JSON.stringify(this.guests) !== JSON.stringify(this.invite.guests)
    },

    dateInviteConfirmation() {
      return this.invite.date || this.confirmationDate
    },

    childAgeLabel() {
      const maximumAge = this.configs?.preferences?.closed?.minimum_age_child
      if (maximumAge >= 1) {
        return `Criança - até ${maximumAge} ano${maximumAge > 1 ? 's' : ''}`
      }
      return 'Criança'
    },

    agesList() {
      return [
        { value: 'adult', text: 'Adulto' },
        { value: 'child', text: this.childAgeLabel },
      ]
    },
  },

  data() {
    return {
      guests: [],
      use_terms: false,
      obs: '',
      email: '',
      statusList: [
        { value: 'yes', text: 'Irá comparecer' },
        { value: 'no', text: 'Não irá comparecer' },
      ],
      loading: false,
      isConfirmedInvite: false,
      confirmationDate: new Date(),
    }
  },

  mounted() {
    const guestsList = JSON.parse(JSON.stringify(this.invite.guests))
    this.guests = guestsList.length > 0 ? guestsList : [{ ...GUEST }]
    this.isConfirmedInvite = !!this.invite?.date
    this.validation.reset()
  },

  methods: {
    getGuestFieldErrorByIndex(field, index) {
      return (
        this.guestsTouched &&
        this.guestErrors.has &&
        this.guestErrors.errors[index][field] &&
        !this.isEmptyGuest(this.guests[index])
      )
    },

    isEmptyGuest(guest) {
      return JSON.stringify(GUEST) === JSON.stringify(guest)
    },

    addGuest() {
      if (this.guests.length < this.invite.max_guests) {
        this.guests.push({ ...GUEST })
      }
    },

    submit() {
      if (!this.canSubmit) return

      this.$validate().then((success) => {
        this.loading = true
        if (success) {
          const noToken = undefined
          const { wed_id } = this.$route.query
          const api = new RsvpApi(noToken, wed_id)

          api
            .answerInvite(this.invite.id, {
              id: this.invite.id,
              wed_id,
              phone: this.invite.phone,
              obs: this.obs,
              email: this.email,
              use_terms: this.use_terms,
              guests: this.guests.filter((guest) => !this.isEmptyGuest(guest)),
            })
            .then((response) => {
              const invite = response?.data?.data
              this.isConfirmedInvite = true
              this.confirmationDate = invite?.date || new Date()
              window.parent.postMessage(
                {
                  event: 'invite-confirmed',
                  id: this.invite.id,
                },
                '*'
              )
            })
            .finally(() => {
              this.loading = false
            })
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.c-invite-confirmation {
  .title {
    font-size: 20px;
    font-weight: 700;
    color: $neutraldarkest;
  }

  .description {
    font-size: 16px;
    font-weight: bold;
    color: $neutral;
    margin: 32px 0;
    &.final {
      margin: 16px 0 32px;
      font-weight: bold;
    }
  }

  .guest {
    .separator {
      width: 100%;
      height: 1px;
      margin: 32px 0;
      background-color: #d9e2eb;
    }
  }

  .add-guest {
    padding: 32px 0;

    .error-label {
      color: $error;
      font-size: 16px;
      strong {
        color: inherit;
      }
    }
  }
}
</style>
